// skills/cooking/cooking.js
// Cooking skill logic

import { FOOD, TOOLS, EMPOWERED_RECIPES } from './cookingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import {
    getTalentLootBonus,
    getTalentFlatBonus,
    rollTalentProc,
    incrementStackCount,
    resetStackCount
} from '../../systems/talentTree/talentTree.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class CookingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.COOKING, TOOLS, 'BASIC');
        // Mise en Place: remaining fast cooks after ingredient save
        this.miseEnPlaceCount = 0;
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill.getCurrentTool)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Calculate effective speed multiplier including tool, mastery, and talent bonuses
     * Overrides BaseSkill to add Mise en Place talent support
     */
    getEffectiveSpeedMultiplier() {
        let multiplier = super.getEffectiveSpeedMultiplier();

        // Apply Mise en Place bonus (25% faster for next 3 cooks after ingredient save)
        if (this.miseEnPlaceCount > 0) {
            multiplier *= 1.25;
        }

        return multiplier;
    }

    /**
     * Roll for talent proc effects and apply them
     * Returns object with proc results
     */
    rollCookingProcs() {
        const procs = {
            ingredientSave: false,
            doubleFood: false,
            banquet: false
        };

        // Ingredient save - 5% per rank
        if (rollTalentProc(SKILLS.COOKING, 'ingredientSave')) {
            procs.ingredientSave = true;
        }

        // Banquet - 4% chance for 5x food (higher priority than double)
        if (rollTalentProc(SKILLS.COOKING, 'banquet')) {
            procs.banquet = true;
        }
        // Double food - 5% per rank (only if no banquet)
        else if (rollTalentProc(SKILLS.COOKING, 'doubleFood')) {
            procs.doubleFood = true;
        }

        return procs;
    }

    canCook(foodId) {
        // Find food by id property, not by object key
        const food = Object.values(FOOD).find(f => f.id === foodId);
        if (!food) return { success: false, reason: 'Food not found' };

        const skill = gameState.getSkill(SKILLS.COOKING);
        if (skill.level < food.level) {
            return { success: false, reason: `Requires level ${food.level} Cooking` };
        }

        // Check for required raw food in inventory
        const hasFood = gameState.getItemCount(food.inputItem) >= food.inputQuantity;
        if (!hasFood) {
            const itemName = food.inputItem.replace(/_/g, ' ');
            return { success: false, reason: `Need ${food.inputQuantity} ${itemName}` };
        }

        // Check if output can fit in inventory
        const outputItem = food.drops[0]?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    startCooking(foodId, showToast = true) {
        // Find food by id property, not by object key
        const food = Object.values(FOOD).find(f => f.id === foodId);
        const canCook = this.canCook(foodId);

        if (!canCook.success) {
            if (showToast) toastWarning(canCook.reason);
            return false;
        }

        // Calculate cooking time including talent bonuses
        const baseTime = food.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const cookingTime = baseTime / speedMultiplier;

        const action = {
            type: 'cooking',
            name: `Cooking ${food.name}`,
            food: food,
            foodId: foodId.toUpperCase(),
            progress: 0,
            duration: cookingTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param allows admin panel to speed up actions
            tick: (speedMultiplier = 1) => {
                // Check if we still have materials - stop immediately if not
                const hasMaterials = gameState.getItemCount(food.inputItem) >= food.inputQuantity;
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeCooking(food);
                    const canContinue = this.canCook(foodId);
                    if (canContinue.success) {
                        this.startCooking(foodId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeCooking(food);
                    const canContinue = this.canCook(foodId);
                    if (canContinue.success) {
                        this.startCooking(foodId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    completeCooking(food) {
        // Roll for talent procs first
        const procs = this.rollCookingProcs();

        // Get output item for proc notifications
        const outputItemId = food.drops[0]?.itemId;

        // Consume raw food from inventory (unless ingredient save proc)
        if (!procs.ingredientSave) {
            const removed = gameState.removeItem(food.inputItem, food.inputQuantity);
            if (!removed) {
                logger.warn('Failed to remove raw food for cooking');
                this.stopCooking();
                return;
            }
        } else {
            recordTalentProc(SKILLS.COOKING, 'Efficient Cooking', 'Ingredients saved!', null);
            // Mise en Place: ingredient save triggers 3 fast cooks
            const hasMiseEnPlace = getTalentFlatBonus(SKILLS.COOKING, 'miseEnPlace') > 0;
            if (hasMiseEnPlace) {
                this.miseEnPlaceCount = 3;
                recordTalentProc(SKILLS.COOKING, 'Mise en Place', '3x fast cooks!', null);
            }
        }

        // Decrement Mise en Place counter if active
        if (this.miseEnPlaceCount > 0) {
            this.miseEnPlaceCount--;
        }

        // Pass food.id for admin node XP override
        const xpResult = addXP(SKILLS.COOKING, food.xp, food.id);

        // Award drops (handles corruption loot multiplier centrally)
        awardDrops(SKILLS.COOKING, food.drops);

        // Apply talent proc bonuses
        if (procs.banquet) {
            // Banquet = 5x total (already got 1, add 4 more)
            for (let i = 0; i < 4; i++) {
                awardDrops(SKILLS.COOKING, food.drops);
            }
            recordTalentProc(SKILLS.COOKING, 'Banquet', null, null, { itemId: outputItemId, quantity: 5 });
        } else if (procs.doubleFood) {
            awardDrops(SKILLS.COOKING, food.drops);
            recordTalentProc(SKILLS.COOKING, 'Batch Cooking', null, null, { itemId: outputItemId, quantity: 2 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.COOKING);

        // Check for pilferer contract assistance (rolls once per action completion)
        pilferingSkill.tryContractAssist('cooking');

        // Increment stacking speed count (Kitchen Flow talent)
        incrementStackCount(SKILLS.COOKING, food.id);

        this.onCookingComplete(food, xpResult);
    }

    stopCooking() {
        resetStackCount(SKILLS.COOKING);
        this.miseEnPlaceCount = 0;
        gameState.clearAction();
    }

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.COOKING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Cooking` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.COOKING, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.COOKING, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.COOKING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.COOKING, toolId);
        return { success: true, action: 'purchased' };
    }

    getAvailableFood() {
        const skill = gameState.getSkill(SKILLS.COOKING);
        return Object.values(FOOD).filter(food => food.level <= skill.level);
    }

    // ==================== EMPOWERING ====================

    /**
     * Check if player can empower a recipe
     * @param {string} recipeId - Recipe ID (e.g., 'empowered_sand_smelt_cook')
     * @returns {{ success: boolean, reason?: string, missingItems?: Array }}
     */
    canEmpower(recipeId) {
        const recipe = Object.values(EMPOWERED_RECIPES).find(r => r.id === recipeId);
        if (!recipe) return { success: false, reason: 'Recipe not found' };

        const skill = gameState.getSkill(SKILLS.COOKING);
        if (skill.level < recipe.level) {
            return { success: false, reason: `Requires level ${recipe.level} Cooking` };
        }

        // Check all input items (inventory + BloomHold for eligible items)
        const missingItems = [];
        for (const input of recipe.inputs) {
            const inventoryQty = gameState.getItemCount(input.itemId);
            // Check BloomHold for eligible items (crops)
            const bloomholdQty = (gameState.isBloomholdUnlocked() && isBloomholdEligible(input.itemId))
                ? gameState.getBloomholdItemCount(input.itemId)
                : 0;
            const totalQty = inventoryQty + bloomholdQty;
            if (totalQty < input.quantity) {
                const itemName = input.itemId.replace(/_/g, ' ');
                missingItems.push(`${itemName}: ${totalQty}/${input.quantity}`);
            }
        }

        if (missingItems.length > 0) {
            return { success: false, reason: `Missing: ${missingItems.join(', ')}`, missingItems };
        }

        // Check if output can fit in inventory
        const outputItem = recipe.output?.itemId;
        if (outputItem && !canFitItem(outputItem)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start empowering a recipe
     * @param {string} recipeId - Recipe ID
     * @param {boolean} showToast - Show toast on failure
     * @returns {boolean} Success
     */
    startEmpowering(recipeId, showToast = true) {
        const recipe = Object.values(EMPOWERED_RECIPES).find(r => r.id === recipeId);
        const canEmpower = this.canEmpower(recipeId);

        if (!canEmpower.success) {
            if (showToast) toastWarning(canEmpower.reason);
            return false;
        }

        // Calculate empowering time including talent bonuses
        const baseTime = recipe.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const empowerTime = baseTime / speedMultiplier;

        const action = {
            type: 'empowering',
            name: `Empowering ${recipe.name.replace(/^Empowered\s+/, '')}`,
            recipe: recipe,
            recipeId: recipeId,
            progress: 0,
            duration: empowerTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                // Check if we still have all input materials (inventory + BloomHold)
                let hasMaterials = true;
                for (const input of recipe.inputs) {
                    const inventoryQty = gameState.getItemCount(input.itemId);
                    const bloomholdQty = (gameState.isBloomholdUnlocked() && isBloomholdEligible(input.itemId))
                        ? gameState.getBloomholdItemCount(input.itemId)
                        : 0;
                    if (inventoryQty + bloomholdQty < input.quantity) {
                        hasMaterials = false;
                        break;
                    }
                }
                if (!hasMaterials) {
                    gameState.clearAction();
                    return;
                }

                // Check for force complete
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeEmpowering(recipe);
                    const canContinue = this.canEmpower(recipeId);
                    if (canContinue.success) {
                        this.startEmpowering(recipeId, false);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeEmpowering(recipe);
                    const canContinue = this.canEmpower(recipeId);
                    if (canContinue.success) {
                        this.startEmpowering(recipeId, false);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete empowering action
     * @param {Object} recipe - Recipe data
     */
    completeEmpowering(recipe) {
        // Roll for talent procs first
        const procs = this.rollCookingProcs();

        // Get output item for proc notifications
        const outputItemId = recipe.output?.itemId;

        // Consume all input items (unless ingredient save proc)
        // Check inventory first, then BloomHold for eligible items
        if (!procs.ingredientSave) {
            for (const input of recipe.inputs) {
                let remaining = input.quantity;

                // First, consume from inventory
                const inventoryQty = gameState.getItemCount(input.itemId);
                const fromInventory = Math.min(inventoryQty, remaining);
                if (fromInventory > 0) {
                    gameState.removeItem(input.itemId, fromInventory);
                    remaining -= fromInventory;
                }

                // If still need more, consume from BloomHold
                if (remaining > 0 && gameState.isBloomholdUnlocked() && isBloomholdEligible(input.itemId)) {
                    const bloomholdQty = gameState.getBloomholdItemCount(input.itemId);
                    const fromBloomhold = Math.min(bloomholdQty, remaining);
                    if (fromBloomhold > 0) {
                        gameState.removeBloomholdItem(input.itemId, fromBloomhold);
                        remaining -= fromBloomhold;
                    }
                }

                if (remaining > 0) {
                    logger.warn(`Failed to remove ${input.itemId} for empowering`);
                    this.stopEmpowering();
                    return;
                }
            }
        } else {
            recordTalentProc(SKILLS.COOKING, 'Efficient Cooking', 'Ingredients saved!', null);
            // Mise en Place: ingredient save triggers 3 fast cooks
            const hasMiseEnPlace = getTalentFlatBonus(SKILLS.COOKING, 'miseEnPlace') > 0;
            if (hasMiseEnPlace) {
                this.miseEnPlaceCount = 3;
                recordTalentProc(SKILLS.COOKING, 'Mise en Place', '3x fast cooks!', null);
            }
        }

        // Decrement Mise en Place counter if active
        if (this.miseEnPlaceCount > 0) {
            this.miseEnPlaceCount--;
        }

        // Award XP
        const xpResult = addXP(SKILLS.COOKING, recipe.xp, recipe.id);

        // Add output item
        gameState.addItem(recipe.output.itemId, recipe.output.quantity);

        // Apply talent proc bonuses
        if (procs.banquet) {
            // Banquet = 5x total (already got 1, add 4 more)
            for (let i = 0; i < 4; i++) {
                gameState.addItem(recipe.output.itemId, recipe.output.quantity);
            }
            recordTalentProc(SKILLS.COOKING, 'Banquet', null, null, { itemId: outputItemId, quantity: 5 });
        } else if (procs.doubleFood) {
            gameState.addItem(recipe.output.itemId, recipe.output.quantity);
            recordTalentProc(SKILLS.COOKING, 'Batch Cooking', null, null, { itemId: outputItemId, quantity: 2 });
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.COOKING);

        // Check for pilferer contract assistance
        pilferingSkill.tryContractAssist('cooking');

        // Increment stacking speed count (Kitchen Flow talent)
        incrementStackCount(SKILLS.COOKING, recipe.id);

        this.onEmpoweringComplete(recipe, xpResult);
    }

    /**
     * Stop empowering
     */
    stopEmpowering() {
        resetStackCount(SKILLS.COOKING);
        this.miseEnPlaceCount = 0;
        gameState.clearAction();
    }

    /**
     * Get available empower recipes based on level
     * @returns {Array} Array of available recipes
     */
    getAvailableEmpowerRecipes() {
        const skill = gameState.getSkill(SKILLS.COOKING);
        return Object.values(EMPOWERED_RECIPES).filter(recipe => recipe.level <= skill.level);
    }

    /**
     * Callback when empowering completes (set by UI)
     */
    onEmpoweringComplete(recipe, xpResult) {
        logger.game(`Empowered ${recipe.name}! +${recipe.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Cooking level up! Now level ${xpResult.newLevel}`);
        }
    }

    onCookingComplete(food, xpResult) {
        logger.game(`Cooked ${food.name}! +${food.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Cooking level up! Now level ${xpResult.newLevel}`);
        }
    }
}

export const cookingSkill = new CookingSkill();
export default cookingSkill;